import { useState } from "react";
import type { NextPage } from "next";
import Image from "next/image";
import Link from "next/link";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";

import PageLayout from "~/components/layout/PageLayout";
import Button from "~/components/ui-elements/Button";

const About: NextPage = () => {
  return (
    <>
      <PageLayout
        seo={{
          title: "About",
          description: "Learn more about Vital",
        }}
      >
        <BGImage />
        <Team />
        <Story />
      </PageLayout>
    </>
  );
};

const BGImage = () => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={classnames(
        "relative z-0 flex h-[600px] w-full gap-8 ",
        "flex-col items-center justify-center lg:flex-row",
      )}
    >
      <Image
        fill
        src="/images/vision-bg.jpg"
        alt="Beyond Baseline"
        sizes="100vw"
        onLoadingComplete={() => setLoaded(true)}
        className={classnames(
          loaded ? "opacity-100" : "opacity-0",
          "transition-opacity duration-500",
        )}
      />
      <div className="absolute bottom-0 left-0 right-0 top-0 bg-white/50 dark:bg-black/30" />
      <Vision />
    </div>
  );
};

const Vision = () => (
  <div
    className={classnames(
      "z-10 flex w-full max-w-screen-2xl flex-col items-start justify-center",
      "gap-8 px-16 sm:px-20 md:px-28 lg:flex-row lg:px-32",
    )}
  >
    <div className="flex w-full flex-col items-start gap-2">
      <h1 className="text-4xl font-normal md:text-5xl">Take your health</h1>
      <h1 className="text-4xl font-semibold italic md:text-5xl">
        beyond baseline
      </h1>
    </div>
    <div className="flex w-full flex-col items-start gap-2">
      <p className="text-lg font-normal">
        To pursue your best self, you need to{" "}
        <span className="text-lg font-semibold">
          make health a top priority.
        </span>
      </p>
      <p className="text-lg font-normal">
        With a brain and body in optimal shape, everything is easier and
        anything is possible.
      </p>
    </div>
  </div>
);

const Story = () => (
  <div className="z-10 flex w-full items-center justify-center bg-base-300">
    <div
      className={classnames(
        "flex w-full max-w-screen-2xl justify-center",
        "gap-8 p-16 sm:gap-12 sm:p-20 md:gap-16 md:p-28 lg:p-32",
        "flex-col items-start sm:flex-row sm:items-center",
      )}
    >
      <div
        className={classnames(
          "relative flex aspect-square shrink-0 overflow-hidden rounded-2xl bg-base-200 ",
          "h-32 w-full sm:w-32 md:h-64 md:w-64",
        )}
      >
        <Image
          fill
          src="/images/blog/cortina.jpg"
          alt="Joe Sobrero Climbing"
          className="object-cover"
          sizes="(min-width: 768px) 32rem, 100vw"
          quality={100}
        />
      </div>
      <div className="flex w-full flex-col gap-2">
        <h1 className="text-3xl font-semibold md:text-4xl lg:text-5xl">
          How it started
        </h1>
        <p className="text-sm font-normal md:text-base lg:text-lg">
          Read about Joe and and how his battle with health drives the vision
          for Vital Routines
        </p>
        <Link href="/blog/600333ff-109f-4995-8e37-02e76ad2495f">
          <Button className="btn btn-primary mt-2" variant="outline">
            Read the story
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

const Team = () => (
  <div
    className={classnames(
      "z-10 flex w-full max-w-screen-2xl flex-col bg-base-100",
      "gap-16 p-16 sm:p-20 md:p-28 lg:p-32",
    )}
  >
    <div className="flex flex-col gap-0">
      <h2 className="text-3xl font-semibold">Our Team</h2>
    </div>
    <div className="flex w-full flex-col gap-16 lg:flex-row">
      <div className="flex w-full flex-row items-center justify-start gap-8">
        <div className="relative flex aspect-square h-32 shrink-0 overflow-hidden rounded-2xl bg-base-200">
          <Image
            fill
            src="/images/joseph-sobrero.png"
            alt="Joe Sobrero Portrait"
            sizes="128px"
            quality={100}
          />
        </div>
        <div className="flex flex-col">
          <h6 className="text-xl font-semibold">Joe Sobrero</h6>
          <p className="mb-2 text-lg font-normal">Co-Founder & CEO</p>
          <div className="flex flex-row gap-2 text-base-content/80">
            <a
              href="https://x.com/changemultiply/"
              target="_blank"
              className="h-4 w-4 leading-none hover:text-base-content/50"
              rel="noreferrer"
            >
              𝕏
            </a>
            <a
              href="https://linkedin.com/in/josephsobrero/"
              target="_blank"
              className="hover:text-base-content/50"
              rel="noreferrer"
            >
              <FontAwesomeIcon icon={faLinkedin} className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-row items-center justify-start gap-8">
        <div className="relative flex aspect-square h-32 shrink-0 overflow-hidden rounded-lg bg-base-200">
          <Image
            fill
            src="/images/nate-furbeyre.png"
            alt="Nate Furbeyre Portrait"
            sizes="128px"
            quality={100}
          />
        </div>
        <div className="flex flex-col ">
          <h6 className="text-xl font-semibold">Nate Furbeyre</h6>
          <p className="mb-2 text-lg font-normal">Co-Founder & CTO</p>
          <div className="flex flex-row gap-2 text-base-content/80">
            <a
              className="h-4 w-4 leading-none hover:text-base-content/50"
              href="https://x.com/nmfurbearr"
              target="_blank"
              rel="noreferrer"
            >
              𝕏
            </a>
            <a
              href="https://www.linkedin.com/in/nathaniel-furbeyre/"
              target="_blank"
              className="hover:text-base-content/50"
              rel="noreferrer"
            >
              <FontAwesomeIcon icon={faLinkedin} className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default About;
